import React from 'react';
import { Neo, NeoGrid, Views } from '@singularsystems/neo-react';
import DividendProcessingVM from './DividendProcessingVM';
import { observer } from 'mobx-react';
import DividendSummaryLookup from '../../Models/Dividends/Lookups/DividendSummaryLookup';
import DividendAccrualDetailLookup from '../../Models/Dividends/Lookups/DividendAccrualDetailLookup';
import { DividendAccrualDetailInfo } from '../../Models/Dividends/Lookups/DividendAccrualDetailResponse';
import { DateUtils, ModalUtils, NumberUtils } from '@singularsystems/neo-core';
import * as Roles from '../../Models/Security/Roles'
import TextValues from '../../Services/TextValues';
import IssuerInvestmentAutoComplete from '../../Components/IssuerInvestmentAutoComplete';

@observer
export default class DividendProcessingView extends Views.ViewBase<DividendProcessingVM> {

    constructor(props: unknown) {
        super("Dividend Processing", DividendProcessingVM, props);
    }

    public render() {

        return (
            <div className="mt-4 mb-4">
                {!this.viewModel.currentlyProcessing &&
                    <>
                        <Neo.Card title="Criteria">
                            <div className="row">
                                <Neo.FormGroupInline bind={this.viewModel.summaryCriteria.meta.entityId} className="col-lg-3"
                                    select={{ itemSource: this.viewModel.dataCache.entities }}
                                    label="Entity" />
                                <Neo.FormGroupInline bind={this.viewModel.summaryCriteria.meta.startDate} className="col-lg-3" />
                                <Neo.FormGroupInline bind={this.viewModel.summaryCriteria.meta.endDate} className="col-lg-3" />
                                <Neo.FormGroupInline bindContext={this.viewModel.summaryCriteria.meta.issuerInvestmentId} className="col-lg-3">
                                    <IssuerInvestmentAutoComplete
                                        bind={this.viewModel.summaryCriteria.meta.issuerInvestmentId}
                                        bindDisplay={this.viewModel.summaryCriteria.meta.issuerInvestmentName}
                                        entityId={this.viewModel.summaryCriteria.entityId}
                                        useParentCache={false} />
                                </Neo.FormGroupInline>
                            </div>
                        </Neo.Card>
                        <Neo.Card title={`${TextValues.distribution} Processing`}>
                            <NeoGrid.Grid<DividendSummaryLookup> items={this.viewModel.dividendSummaryList}>
                                {(item, meta) => (
                                    <NeoGrid.Row>
                                        <NeoGrid.Column display={meta.startDate} label={`${TextValues.distribution} Date`} />
                                        <NeoGrid.Column display={meta.expectedDividend} label="Expected Amount" sum />
                                        <NeoGrid.Column display={meta.actualDividend} sum label="Processed Amount" />
                                        <NeoGrid.Column display={meta.totalCount} sum />
                                        <NeoGrid.Column className="ps-3">
                                            {item.unprocessedCount > 0 && <Neo.Badge variant="dark" isPill className="me-1"><span>{item.unprocessedCount}</span> Unprocessed </Neo.Badge>}
                                            {item.missingLetters > 0 && <Neo.Badge variant="primary" isPill className="me-1"><span>{item.missingLetters}</span> Missing letters </Neo.Badge>}
                                            {item.missingExports > 0 && <Neo.Badge variant="info" isPill className="me-1"><span>{item.missingExports}</span> Not exported </Neo.Badge>}
                                        </NeoGrid.Column>
                                        <NeoGrid.ButtonColumn editButton={{
                                            text: "View",
                                            icon: "arrow-right",
                                            iconAlignment: "right",
                                            onClick: () => this.viewModel.loadAccrualDetails(item)
                                        }} />
                                    </NeoGrid.Row>
                                )}
                            </NeoGrid.Grid>
                        </Neo.Card>
                    </>}

                {this.viewModel.currentlyProcessing && this.viewModel.currentInfo &&
                    this.renderDividendDetail(this.viewModel.currentlyProcessing, this.viewModel.currentInfo)}
            </div>
        );
    }

    private renderDividendDetail(details: DividendSummaryLookup, info: DividendAccrualDetailInfo) {
        const canProcess = this.viewModel.authorisationService.hasRole(Roles.DividendProcessing.Process);

        return (
            <div className="mt-4 mb-4">
                <Neo.Card title="Criteria">
                    <div className="row">
                        <div className="col-lg-2">
                            <Neo.Button icon="arrow-left" variant="light" onClick={() => this.viewModel.back()}>Back</Neo.Button>
                        </div>
                        <div className="col-lg-3">
                            <Neo.FormGroupInline display={this.viewModel.accrualDetailCriteria.meta.entityId}
                                select={{ itemSource: this.viewModel.dataCache.entities }}
                                label='Entity' />
                        </div>
                        <div className="col-lg-3">
                            <Neo.FormGroupInline display={this.viewModel.accrualDetailCriteria.meta.startDate} className="mb-0" />
                        </div>
                        <div className="col-lg-4 text-end">
                            <Neo.Button disabled={!canProcess} onClick={() => this.processDividendAccruals(details, info)} icon="sync">
                                Process
                            </Neo.Button>
                        </div>
                    </div>
                </Neo.Card>
                <Neo.Card title={`${TextValues.distribution} Processing`}>

                    {info.hasExpiredExemptions &&
                        <Neo.Alert variant="warning" heading="Exemptions">
                            Note: some of the investors in this {`${TextValues.distributionLowerCase}`} have missing, or expired exemptions. DWT will be withheld at the full rate for these investors.
                        </Neo.Alert>}

                    <Neo.Pager pageManager={this.viewModel.pageManager} pageControls="bottom" >
                        <NeoGrid.Grid<DividendAccrualDetailLookup>>
                            {(item, meta) => (
                                <NeoGrid.Row>
                                    <NeoGrid.Column display={meta.investmentRefNo} width={135} />
                                    <NeoGrid.Column display={meta.investorName} />
                                    <NeoGrid.Column display={meta.expectedDividend} label={`Expected ${TextValues.distribution}`} footerContent={() => {
                                        if (this.viewModel.currentlyProcessing) {
                                            return NumberUtils.format(this.viewModel.currentlyProcessing.meta.expectedDividend)
                                        }
                                        else return undefined;
                                    }} />
                                    <NeoGrid.Column display={meta.actualDividend} footerContent={() => {
                                        if (this.viewModel.currentlyProcessing) {
                                            return NumberUtils.format(this.viewModel.currentlyProcessing.meta.actualDividend)
                                        }
                                        else return undefined;
                                    }} label="Processed Amount" />
                                    {info.willReinvest &&
                                        <NeoGrid.Column display={meta.reInvest} label="Reinvest?" />}
                                    {info.hasExpiredExemptions &&
                                        <NeoGrid.ButtonColumn>
                                            {item.exemptionWarning !== null && <i className="fa fa-exclamation-triangle fa-2x text-warning" data-tip={item.exemptionWarning}></i>}
                                        </NeoGrid.ButtonColumn>}
                                </NeoGrid.Row>
                            )}
                        </NeoGrid.Grid>
                    </Neo.Pager>
                </Neo.Card>
            </div>
        )
    }

    private processDividendAccruals(details: DividendSummaryLookup, info: DividendAccrualDetailInfo) {

        const body = <div>
            Are you sure you want to process {`${TextValues.distributionLowerCase}s`} for {DateUtils.format(details.startDate, "dd MMM yyyy")}?

            {info.hasExpiredExemptions &&
                <Neo.Alert className="mt-3" variant="warning">
                    Warning: some of the investors in this {`${TextValues.distributionLowerCase}`} have missing, or expired exemptions. DWT will be withheld at the full rate for these investors.
                </Neo.Alert>}
        </div>

        ModalUtils.showYesNoDismissible(`Process ${TextValues.distribution}s`, body, () => this.viewModel.processDividendAccruals());
    }
}